import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const POLICY_TYPES = [
  { key: 'rules',   label: '📋 Rules & Regulations', desc: 'General platform rules for all users' },
  { key: 'cashout', label: '💸 Cashout Rules',        desc: 'Cashout policies for all bonus types' },
];

export default function AdminPolicy() {
  const [activeType, setActiveType] = useState('rules');
  const [policies, setPolicies] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    Promise.all(
      POLICY_TYPES.map(t => api.get(`/policies/${t.key}`).then(({ data }) => ({ key: t.key, policy: data.policy })))
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.key] = r.policy; });
      setPolicies(map);
    }).finally(() => setLoading(false));
  }, []);

  const form = policies[activeType] || { title: '', content: '', sections: [] };

  const setForm = (updates) => {
    setPolicies(prev => ({ ...prev, [activeType]: { ...prev[activeType], ...updates } }));
  };

  const addSection = () => {
    const sections = [...(form.sections || []), { heading: '', body: '' }];
    setForm({ sections });
  };

  const updateSection = (i, field, val) => {
    const sections = form.sections.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    setForm({ sections });
  };

  const removeSection = (i) => {
    setForm({ sections: form.sections.filter((_, idx) => idx !== i) });
  };

  const handleSave = async () => {
    setSaving(true); setMessage(null);
    try {
      await api.put(`/policies/${activeType}`, { title: form.title, content: form.content, sections: form.sections });
      setMessage({ type: 'success', text: '✅ Policy saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error saving' });
    } finally { setSaving(false); setTimeout(() => setMessage(null), 3000); }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>POLICIES</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Manage Rules & Regulations and Cashout Policies shown to users</p>
      </div>

      {/* Type selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        {POLICY_TYPES.map(t => (
          <button key={t.key} onClick={() => setActiveType(t.key)} style={{ padding: '14px 24px', borderRadius: 12, border: `2px solid ${activeType === t.key ? '#f5c400' : 'var(--border)'}`, background: activeType === t.key ? 'rgba(245,196,0,0.08)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: activeType === t.key ? '#f5c400' : 'var(--text-primary)', marginBottom: 4 }}>{t.label}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.desc}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Editor */}
        <div>
          {message && (
            <div style={{ background: message.type==='success'?'rgba(16,185,129,0.1)':'rgba(239,68,68,0.1)', border:`1px solid ${message.type==='success'?'#10b981':'#ef4444'}`, borderRadius:8, padding:'10px 14px', color:message.type==='success'?'#10b981':'#ef4444', marginBottom:16, fontSize:14 }}>{message.text}</div>
          )}

          <div className="form-group">
            <label>Page Title</label>
            <input value={form.title || ''} onChange={e => setForm({ title: e.target.value })} placeholder="e.g. Rules & Regulations" />
          </div>

          <div className="form-group">
            <label>Introduction / Summary</label>
            <textarea value={form.content || ''} onChange={e => setForm({ content: e.target.value })} rows={3} placeholder="Brief intro shown at the top of the page..." style={{ resize: 'vertical' }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, letterSpacing: '0.05em' }}>SECTIONS</label>
              <button onClick={addSection} className="btn btn-secondary btn-sm">+ Add Section</button>
            </div>

            {(form.sections || []).map((section, i) => (
              <div key={i} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}>SECTION {i + 1}</span>
                  <button onClick={() => removeSection(i)} style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444', borderRadius: 6, color: '#ef4444', padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label>Heading</label>
                  <input value={section.heading} onChange={e => updateSection(i, 'heading', e.target.value)} placeholder="e.g. Age Requirement" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Content</label>
                  <textarea value={section.body} onChange={e => updateSection(i, 'body', e.target.value)} rows={3} placeholder="Section content..." style={{ resize: 'vertical' }} />
                </div>
              </div>
            ))}

            {(form.sections || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-secondary)', borderRadius: 10, border: '1px dashed var(--border)' }}>
                No sections yet. Click "+ Add Section" to add one.
              </div>
            )}
          </div>

          <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ width: '100%' }}>
            {saving ? '⟳ Saving...' : '💾 Save Policy'}
          </button>
        </div>

        {/* Live Preview */}
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', marginBottom: 14 }}>LIVE PREVIEW</div>
          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 24px', minHeight: 400, maxHeight: 600, overflowY: 'auto' }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 16, background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {form.title || 'Policy Title'}
            </h1>
            {form.content && <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 20, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 8 }}>{form.content}</p>}
            {(form.sections || []).map((s, i) => s.heading || s.body ? (
              <div key={i} style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10 }}>
                {s.heading && <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#f5c400', marginBottom: 8 }}>{s.heading}</h3>}
                {s.body && <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>{s.body}</p>}
              </div>
            ) : null)}
            {(!form.sections || form.sections.length === 0) && !form.content && (
              <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 13 }}>Start editing to see the preview...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
